import { DataGrid } from "@mui/x-data-grid";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import { useState } from "react";
import { GridRowModes } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";

const CustomDataGrid = ({
    columns,
    rows,
    actions = [],
    handleSaveConfirmClick = (row) => {
        console.log(row);
    },
    hasEdit = 1,
    perPage = 15,
    getRowClassName = () => "",
}) => {
    const theme = useTheme();
    const [rowModesModel, setRowModesModel] = useState({}); // Track row editing modes

    const rowHeight = 30;

    // Handle the click of the "Edit" button to switch to edit mode
    const handleEditClick = (id) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: GridRowModes.Edit },
        })); // Set row mode to edit
    };

    // Handle the click of the "Save" button to save row changes
    const handleSaveClick = (row) => {
        setRowModesModel((prev) => ({
            ...prev,
            [row.id]: { mode: GridRowModes.View },
        })); // Set row mode to view if successful
    };

    // Handle the click of the "Cancel" button to revert to the original actions
    const handleCancelClick = (id) => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        })); // Revert to view mode
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel); // Update row modes when editing is toggled
    };

    // Use processRowUpdate to get the edited row data
    const processRowUpdate = async (newRow, oldRow) => {
        try {
            const isSuccessful = await handleSaveConfirmClick(newRow); // Now passing the edited row object
            if (isSuccessful) {
                toast.success("Row saved successfully!"); // Show success toast
                return newRow; // Return the updated data
            } else {
                toast.error("Failed to save the row!"); // Show error toast
                return oldRow; // Revert to old values if not successful
            }
        } catch (error) {
            toast.error("An error occurred while saving the row."); // Show error toast for unexpected errors
            return oldRow; // Return old row if there's an error
        }
    };

    // Render the actions for each row, based on whether it is in edit mode
    const ActionField = {
        field: "Actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 100,
        flex: 1,
        renderCell: (params) => {
            const isInEditMode =
                rowModesModel[params.row.id]?.mode === GridRowModes.Edit;

            // If the row is in edit mode, show Save and Cancel buttons, else show the regular actions
            return (
                <Box>
                    {isInEditMode ? (
                        <>
                            <Tooltip title="Save">
                                <IconButton
                                    color="primary"
                                    sx={{
                                        px: 1,
                                        minWidth: 0,
                                        fontSize: "0.8rem",
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 14,
                                        },
                                    }}
                                    onClick={() => handleSaveClick(params.row)} // Just pass the row as newRow and oldRow
                                >
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton
                                    color="primary"
                                    sx={{
                                        px: 1,
                                        minWidth: 0,
                                        fontSize: "0.8rem",
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 14,
                                        },
                                    }}
                                    onClick={() =>
                                        handleCancelClick(params.row.id)
                                    }
                                >
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            {hasEdit === 1 && (
                                <Tooltip title="Edit">
                                    <IconButton
                                        color="primary"
                                        sx={{
                                            px: 1,
                                            minWidth: 0,
                                            fontSize: "0.8rem",
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 14,
                                            },
                                        }}
                                        onClick={() =>
                                            handleEditClick(params.row.id)
                                        }
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {actions.map((action, index) => (
                                <Tooltip key={index} title={action.title}>
                                    <IconButton
                                        color={action.color}
                                        sx={{
                                            px: 1,
                                            minWidth: 0,
                                            fontSize: "0.8rem",
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 14,
                                            },
                                        }}
                                        onClick={() =>
                                            action.handler(
                                                ...action.parameters.map(
                                                    (param) => params.row[param]
                                                )
                                            )
                                        }
                                    >
                                        {action.icon}
                                    </IconButton>
                                </Tooltip>
                            ))}
                        </>
                    )}
                </Box>
            );
        },
    };

    const updatedColumns = [...columns, ActionField];
    return (
        <div dir={theme.direction === "rtl" ? "rtl" : "ltr"}>
            <DataGrid
                rows={rows}
                columns={updatedColumns}
                rowHeight={rowHeight}
                pageSizeOptions={[perPage]}
                getRowClassName={getRowClassName} // Apply custom row class names
                disableRowSelectionOnClick
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                processRowUpdate={processRowUpdate} // Capture edited row data
                editMode="row" // Enable row editing
                dir={theme.direction === "rtl" ? "rtl" : "ltr"}
                sx={{
                    "& .MuiDataGrid-cell": {
                        fontSize: "0.75rem", // Decrease font size in cells
                        padding: "0 8px", // Adjust padding for a tighter fit
                    },
                    "& .MuiDataGrid-columnHeader": {
                        fontSize: "0.75rem", // Decrease font size in headers
                    },
                    "& .MuiSvgIcon-root": {
                        fontSize: 18, // Decrease checkbox icon size
                    },
                }}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: perPage,
                        },
                    },
                }}
            />
        </div>
    );
};

export default CustomDataGrid;
